import { keepPreviousData } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { DEBOUNCE_TIME } from '@core/constants/common';
import { useDebounceValue } from '@core/hooks/useDebounceValue';
import type { EntityType, GlobalSearchResponse } from '@core/types/search';
import { hasValue } from '@core/utils/equality';
import { MIN_QUERY_LENGTH } from '@core/utils/isQuestion';

import { useSlabQuery } from '../../hooks/useSlabQuery';

export type EntitySearchTab = 'all' | EntityType;

export type EntitySearchState = {
  entityTypes: EntityType[];
  query: string;
  setQuery: (query: string) => void;
  clearQuery: () => void;
  hasQuery: boolean;
  activeTab: EntitySearchTab;
  setActiveTab: (tab: EntitySearchTab) => void;
  searchResults: GlobalSearchResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  getCount: (type: EntityType) => number;
  totalCount: number;
};

type Args = {
  entityTypes: EntityType[];
  limitPerEntity?: number;
};

export const useEntitySearch = ({ entityTypes, limitPerEntity }: Args): EntitySearchState => {
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<EntitySearchTab>('all');

  const debouncedQuery = useDebounceValue(query, DEBOUNCE_TIME);
  const hasQuery = debouncedQuery.length >= MIN_QUERY_LENGTH;

  const {
    data: searchResults,
    isLoading,
    isError,
  } = useSlabQuery(
    'GET global search',
    { queryParams: { query: debouncedQuery, entities: entityTypes, limitPerEntity } },
    { enabled: hasQuery, placeholderData: keepPreviousData },
  );

  const clearQuery = useCallback(() => {
    setQuery('');
    setActiveTab('all');
  }, []);

  const getCount = useCallback(
    (type: EntityType): number => {
      if (!hasQuery || !hasValue(searchResults)) return 0;
      return searchResults.result[type]?.totalCount ?? 0;
    },
    [hasQuery, searchResults],
  );

  const totalCount = entityTypes.reduce((sum, type) => sum + getCount(type), 0);

  return {
    entityTypes,
    query,
    setQuery,
    clearQuery,
    hasQuery,
    activeTab,
    setActiveTab,
    searchResults,
    isLoading,
    isError,
    getCount,
    totalCount,
  };
};
